/**
 * GlobalSearchBar — site-wide search lives in the header, reachable from
 * every page. Self-contained data fetching (lazy on focus) so it doesn't
 * depend on the menu page's in-memory pool.
 *
 * Surfaces (in order):
 *   1. Recent searches (when input empty)
 *   2. Strains — e.g. typing "blue dream" navigates to the strain page
 *   3. Brands — navigates to /menu/{store}/brand/{slug}
 *   4. Products — navigates to /menu/{store}/{slug}
 *   5. "See all results for …" → /menu/{store}?q={term}
 *
 * Mobile UX: collapsed to a search icon by default. Tap to expand to a
 * full-width overlay input. Esc / outside-click / route change collapses.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Search, X } from "lucide-react";

import { getMenu, getStrainLibrary } from "../lib/api";
import { brandSlug } from "../lib/brand";
import { getSelectedStore } from "./HeaderLocationPicker";
import {
    loadHistory,
    saveHistoryEntry,
    clearHistory,
    scoreMatch,
} from "./menu-search/searchHelpers";
import GlobalSearchDropdown from "./menu-search/GlobalSearchDropdown";

export default function GlobalSearchBar({ variant = "desktop", onExpandedChange }) {
    const navigate = useNavigate();
    const location = useLocation();
    const [value, setValue] = useState("");
    const [focused, setFocused] = useState(false);
    const [expanded, setExpanded] = useState(false); // mobile inline-expand
    const [history, setHistory] = useState(() => loadHistory());
    const [highlight, setHighlight] = useState(-1);
    const [pool, setPool] = useState(null);
    const [strains, setStrains] = useState([]);
    const wrapRef = useRef(null);
    const inputRef = useRef(null);

    const storeSlug = getSelectedStore().slug;

    // Lazy-fetch the pool + strains only when the user actually focuses the
    // search box. This avoids a 2000-row Dutchie roundtrip on every page
    // load.
    useEffect(() => {
        if (!focused && !expanded) return;
        if (pool && strains.length) return;
        let cancelled = false;
        if (!pool) {
            getMenu(storeSlug, {
                limit: 2000,
                offset: 0,
                sort_key: "POPULAR",
                sort_direction: "DESC",
            })
                .then((d) => !cancelled && setPool(d?.products || []))
                .catch(() => {});
        }
        if (!strains.length) {
            getStrainLibrary(storeSlug)
                .then((d) => !cancelled && setStrains(d?.strains || []))
                .catch(() => {});
        }
        return () => {
            cancelled = true;
        };
    }, [focused, expanded, storeSlug, pool, strains.length]);

    // Notify parent (Header) when the search is "in use" (focused/value) so
    // it can collapse other navbar items and hand us the full width.
    useEffect(() => {
        if (typeof onExpandedChange !== "function") return;
        if (variant === "desktop") {
            onExpandedChange(focused || !!value);
        }
    }, [focused, value, variant, onExpandedChange]);

    // Close dropdown + collapse on route change.
    useEffect(() => {
        Promise.resolve().then(() => {
            setFocused(false);
            setExpanded(false);
        });
    }, [location.pathname, location.search]);

    // Click-away.
    useEffect(() => {
        const handle = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) {
                setFocused(false);
                setExpanded(false);
            }
        };
        document.addEventListener("mousedown", handle);
        return () => document.removeEventListener("mousedown", handle);
    }, []);

    const q = (value || "").trim().toLowerCase();
    const open = (focused || expanded) && (q.length > 0 || history.length > 0);

    const { brandHits, productHits, strainHits } = useMemo(() => {
        if (!q) return { brandHits: [], productHits: [], strainHits: [] };
        const list = pool || [];

        // Strain matches against the strain library only (display name +
        // slug fragments). Cheap — strain libraries are typically <50 items.
        const sHits = [];
        for (const s of strains) {
            const score = Math.max(
                scoreMatch(s?.name || "", q),
                scoreMatch((s?.slug || "").replace(/-/g, " "), q)
            );
            if (score > 0) sHits.push({ s, score });
        }
        sHits.sort((a, b) => b.score - a.score || b.s.product_count - a.s.product_count);

        // Brands
        const brandMap = new Map();
        for (const p of list) {
            const bn = p?.brand?.name;
            if (!bn) continue;
            const score = scoreMatch(bn, q);
            if (score <= 0) continue;
            const cur = brandMap.get(bn) || { name: bn, count: 0, score: 0 };
            cur.count += 1;
            cur.score = Math.max(cur.score, score);
            brandMap.set(bn, cur);
        }
        const brands = Array.from(brandMap.values())
            .sort((a, b) => b.score - a.score || b.count - a.count)
            .slice(0, 4);

        // Products
        const seen = new Set();
        const products = [];
        for (const p of list) {
            const ns = scoreMatch(p?.name || "", q);
            const bs = scoreMatch(p?.brand?.name || "", q);
            const best = Math.max(ns, bs);
            if (best <= 0) continue;
            const key = p?.id || p?.slug || p?.name;
            if (seen.has(key)) continue;
            seen.add(key);
            products.push({ p, score: best });
        }
        products.sort((a, b) => b.score - a.score);

        return {
            brandHits: brands,
            productHits: products.slice(0, 5).map((x) => x.p),
            strainHits: sHits.slice(0, 4).map((x) => x.s),
        };
    }, [pool, strains, q]);

    // Selectable rows for keyboard navigation.
    const rows = useMemo(() => {
        const out = [];
        if (!q && history.length > 0) {
            history.forEach((h) =>
                out.push({ type: "history", value: h, key: `h:${h}` })
            );
            return out;
        }
        strainHits.forEach((s) =>
            out.push({ type: "strain", value: s, key: `s:${s.slug}` })
        );
        brandHits.forEach((b) =>
            out.push({ type: "brand", value: b, key: `b:${b.name}` })
        );
        productHits.forEach((p) =>
            out.push({ type: "product", value: p, key: `p:${p.id || p.slug}` })
        );
        if (q) out.push({ type: "commit", value: q, key: `c:${q}` });
        return out;
    }, [q, history, strainHits, brandHits, productHits]);

    useEffect(() => {
        Promise.resolve().then(() => setHighlight(-1));
    }, [rows.length, q]);

    const close = () => {
        setFocused(false);
        setExpanded(false);
        setValue("");
        inputRef.current?.blur();
    };

    const commitSearch = (text) => {
        const v = (text || "").trim();
        if (!v) {
            close();
            return;
        }
        saveHistoryEntry(v);
        setHistory(loadHistory());
        navigate(`/menu/${storeSlug}?q=${encodeURIComponent(v)}`);
        close();
    };

    const activateRow = (row) => {
        if (!row) return;
        if (row.type === "history") {
            setValue(row.value);
            commitSearch(row.value);
        } else if (row.type === "strain") {
            navigate(`/menu/${storeSlug}/strain/${row.value.slug}`);
            close();
        } else if (row.type === "brand") {
            navigate(`/menu/${storeSlug}/brand/${brandSlug(row.value.name)}`);
            close();
        } else if (row.type === "product") {
            navigate(`/menu/${storeSlug}/${row.value.slug}`);
            close();
        } else if (row.type === "commit") {
            commitSearch(row.value);
        }
    };

    const onKeyDown = (e) => {
        if (e.key === "Escape") {
            e.preventDefault();
            close();
            return;
        }
        if (!open) return;
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setHighlight((h) => Math.min(rows.length - 1, h + 1));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setHighlight((h) => Math.max(-1, h - 1));
        } else if (e.key === "Enter") {
            e.preventDefault();
            if (highlight >= 0 && rows[highlight]) activateRow(rows[highlight]);
            else commitSearch(value);
        }
    };

    const inputClasses =
        "w-full bg-white/85 border border-zen-ink/15 hover:border-zen-ink/30 focus:border-zen-olive text-zen-ink placeholder:text-zen-muted pl-9 pr-9 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zen-olive/15 transition-colors";

    // ---- DESKTOP variant — inline input bar in header row ----------------
    if (variant === "desktop") {
        return (
            <div
                ref={wrapRef}
                className="relative w-full max-w-md"
                data-testid="global-search-desktop"
            >
                <Search
                    size={14}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-zen-olive pointer-events-none z-[1]"
                />
                <input
                    ref={inputRef}
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    onFocus={() => setFocused(true)}
                    onKeyDown={onKeyDown}
                    placeholder="Search strains, brands, products…"
                    data-testid="global-search-input"
                    autoComplete="off"
                    className={inputClasses}
                />
                {value && (
                    <button
                        type="button"
                        onClick={() => {
                            setValue("");
                            inputRef.current?.focus();
                        }}
                        aria-label="Clear search"
                        data-testid="global-search-clear"
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 text-zen-ink-soft hover:text-zen-ink"
                    >
                        <X size={14} />
                    </button>
                )}
                {open && (
                    <GlobalSearchDropdown
                        q={q}
                        value={value}
                        history={history}
                        rows={rows}
                        highlight={highlight}
                        strainHits={strainHits}
                        brandHits={brandHits}
                        productHits={productHits}
                        storeSlug={storeSlug}
                        onClearHistory={() => {
                            clearHistory();
                            setHistory([]);
                        }}
                        onActivateRow={activateRow}
                        onCommit={commitSearch}
                    />
                )}
            </div>
        );
    }

    // ---- MOBILE variant — icon button → full-row inline expand -----------
    return (
        <div ref={wrapRef} className="relative" data-testid="global-search-mobile">
            {!expanded ? (
                <button
                    type="button"
                    onClick={() => {
                        setExpanded(true);
                        // Focus after expand renders the input.
                        Promise.resolve().then(() =>
                            setTimeout(() => inputRef.current?.focus(), 0)
                        );
                    }}
                    aria-label="Open search"
                    data-testid="global-search-mobile-toggle"
                    className="p-3 text-zen-ink hover:text-zen-olive transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
                >
                    <Search size={20} />
                </button>
            ) : (
                <div className="fixed inset-x-0 top-0 z-[80] bg-zen-bg border-b border-zen-ink/10 px-4 py-3 flex items-center gap-2">
                    <div className="relative flex-1">
                        <Search
                            size={14}
                            className="absolute left-3 top-1/2 -translate-y-1/2 text-zen-olive pointer-events-none z-[1]"
                        />
                        <input
                            ref={inputRef}
                            value={value}
                            onChange={(e) => setValue(e.target.value)}
                            onFocus={() => setFocused(true)}
                            onKeyDown={onKeyDown}
                            placeholder="Search strains, brands, products…"
                            data-testid="global-search-mobile-input"
                            autoComplete="off"
                            className={inputClasses}
                        />
                        {value && (
                            <button
                                type="button"
                                onClick={() => {
                                    setValue("");
                                    inputRef.current?.focus();
                                }}
                                aria-label="Clear"
                                className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 text-zen-ink-soft hover:text-zen-ink"
                            >
                                <X size={14} />
                            </button>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={close}
                        data-testid="global-search-mobile-close"
                        className="px-3 py-2 text-[11px] uppercase tracking-[0.22em] text-zen-ink-soft hover:text-zen-ink min-h-[44px]"
                    >
                        Cancel
                    </button>
                    {open && (
                        <div className="absolute left-0 right-0 top-full">
                            <GlobalSearchDropdown
                                q={q}
                                value={value}
                                history={history}
                                rows={rows}
                                highlight={highlight}
                                strainHits={strainHits}
                                brandHits={brandHits}
                                productHits={productHits}
                                storeSlug={storeSlug}
                                onClearHistory={() => {
                                    clearHistory();
                                    setHistory([]);
                                }}
                                onActivateRow={activateRow}
                                onCommit={commitSearch}
                                inline
                            />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
